<script setup>
	import '~/assets/css/plant-page.css';
    import {isPlantPhotoUuid} from '~/utils/culture-illustration.mjs';
	import '~/assets/css/plant-card.css';
	import { plantCharacteristics } from '~/utils/plant-characteristics.mjs';
    import {selectionLabel,numericRange} from '~/utils/early-tomatoes.mjs';
	import { plantSchema, serializePlantSchema } from '~/utils/plant-schema.mjs';
	import { plantBreadcrumbs } from '~/utils/plant-category-context.mjs';
	import { MONTHS, SEED_SPEC_VALUES } from '~/constants';
	import { useCurrentIndex } from '~/composables/useCurrentIndex';
	import { useProductModals } from '~/composables/useProductModals';

	import { useGetSeedQuery, useRemoveImageMutation, useRemoveSimilarMutation, useRemoveRecordMutation, useInsertFavoriteMutation } from '@/composables/urql';
	import { formattedWeight } from '~/utils/formattedWeight';

	const { HOST_BASE } = useRuntimeConfig().public;
	const client = useUser(); // TODO сделать обертку или получать по ключу

	const route = useRoute()
	const { id } = route.params;

	const { currentIndex, setCurrentIndex } = useCurrentIndex(0);

	const { executeMutation: removeImage } = useRemoveImageMutation();
    const { executeMutation: removeSimilar } = useRemoveSimilarMutation();
    const { executeMutation: removeRecord } = useRemoveRecordMutation();
    const { executeMutation: insertFavorite } = useInsertFavoriteMutation();
	const { executeMutation: removeFavorite } = useRemoveFavoriteMutation();
	const { specs } = await userSeedSpecs();


	const removeImageConfirm = (value) => {
		addModal({
			title: 'Удаление фото',
			body: 'Вы действительно хотите удалить фото? <span class="text-red-600">данное действие нельзя отменить</span>',
			actions: [
				{
					className: 'bg-green-600 text-white',
					title: 'Удалить',
					action: () => {
						removeImage(value);
					},
				},
				{
					title: 'Отмена',
					action: () => {},
				}
			],
		});
	}

	const toggleFavorite = async () => {
		if (!product.value) return;

		// Тут
		const action_id = product.value?.actions[0]?.id;

		if (action_id) {
			await removeFavorite({ id: action_id }, {
				additionalTypenames: ["uogorod_garden_seeds"],
			});

			product.value.actions = [];
		} else {
			const { data } = await insertFavorite({ seed_id: +id }, {
				additionalTypenames: ["uogorod_garden_seeds"],
			});

			product.value.actions = [data.insert_uogorod_users_seed_actions_one];
		}
		await refreshNuxtData('plant-people-counts-'+id);
	}

	// TODO Может быть вместо вызова конкретной модалки можно запускать событие с именем компонента которое нужно вызвать
	const { imagesModal, similarModal, recordModal, reviewModal } = useProductModals();

	const isDescriptionExpanded = ref(false);
	const isMenuExpanded = ref(false);
	const isBigPictureMode = ref(false);

	const { data,executeQuery:reloadProduct } = await useGetSeedQuery({
		variables: {
			id,
			user_id: client.value?.client_id ?? -1
		}
	});
	if (!data.value.uogorod_seeds_by_pk) {
		throw createError({
			statusCode: 404,
			statusMessage: 'Page Not Found'
		});
	}


	const product = computed(() => data.value.uogorod_seeds_by_pk );
  const {data:plantSynonyms,error:plantSynonymsError}=await useFetch('/api/catalog/synonyms',{query:{id}});
  if(plantSynonymsError.value)throw createError({statusCode:503,statusMessage:'Plant synonyms unavailable'});
  const {remember:rememberCatalogPlant}=useCatalogHistory();
  onMounted(()=>rememberCatalogPlant(Number(product.value?.id)));
  const {data:ratingData,error:ratingError}=await useFetch('/api/plant-ratings/summaries',{query:{ids:String(id)}});
  const ratingSummary=computed(()=>ratingData.value?.plants?.[id]);
  const reviewGeneration=useState('plant-review-generation',()=>0);watch(reviewGeneration,()=>reloadProduct({requestPolicy:'network-only'}));
  const facts = computed(() => product.value.params.find(it => it.name == 'facts'));


	const params = computed(() => plantCharacteristics(product.value.params,specs,SEED_SPEC_VALUES).map(p=>{
      const invalidType=p.name==='selection'&&selectionLabel(product.value)==='Тип требует проверки';
      const invalidDays=p.name==='ripening_period'&&!numericRange(product.value,'ripening_period');
      return invalidType||invalidDays?{...p,value:'Требует проверки',values:['Требует проверки']}:p;
    }));
	const badgedParams = computed(() => {
 const category=product.value.category?.slug==='tomat'&&/черри/i.test(product.value.title)?'Томат черри':product.value.category?.title;
 return [category,selectionLabel(product.value)].filter(Boolean);
 });


	// Увеличиваем счетчик просмотров и ставим куку
	if (process.client && !useRuntimeConfig().public.RATINGS_STAGE) {
		fetch(`/api/view?id=${id}&type=seeds`);
	}

	useCatalogSeo(()=>({title:product.value.title+' — описание, фото и отзывы',description:product.value.title+'. '+(product.value.description || 'Описание, характеристики, фотографии и опыт выращивания в отзывах садоводов.'),detail:true}));
    useSeoMeta({ogType:'website',ogImage:()=>isPlantPhotoUuid(product.value.images?.[0]?.uuid) ? HOST_BASE+'/files/uploaded/'+product.value.images[0].uuid+'.webp' : undefined});

	definePageMeta({
		validate
	});

	const breadcrumbs = plantBreadcrumbs(product.value);
const {data:photoVersions}=await useFetch('/api/images/versions',{key:'plant-photo-versions-'+id,query:{seed_id:Number(id)}});
const {data:plantLabel}=await useFetch('/api/catalog/plant-label',{key:'plant-label-'+id,query:{id}});
const schemaCharacteristics=computed(()=>[...params.value,...(product.value.brands?.length?[{name:'manufacturer',label:'Производитель',value:product.value.brands.map(p=>p.brand.title).join(', ')}]:[]),...(product.value.Country?.name?[{name:'manufacturer_country',label:'Страна производителя',value:product.value.Country.name}]:[]),{name:'originator',label:'Оригинатор',value:plantLabel.value?.originator?.title||'Нет подтверждённых данных'}]);
useHead(()=>({script:[{key:'plant-structured-data',type:'application/ld+json',innerHTML:serializePlantSchema(plantSchema(product.value,breadcrumbs,{synonyms:plantSynonyms.value,characteristics:schemaCharacteristics.value,photoVersions:photoVersions.value,ratingSummary:ratingSummary.value,ratingUnavailable:!!ratingError.value}))}]}));
const confirmRemoval=(title,mutation,value)=>addModal({title,body:'Удаление нельзя отменить. Продолжить?',actions:[{title:'Удалить',className:'bg-red-600 text-white',action:()=>mutation(value)},{title:'Отмена',action(){}}]});
</script>
<template>
 <div class="uo-community uo-plant-page plantcard-page">
  <div class="plantcard-breadcrumbs"><CommonBreadcrumbs :links="breadcrumbs" /></div>
  <PlantAd id="yandex_rtb_R-A-20123375-3-site" block-id="R-A-20123375-3" />
  <PlantCard :key="id" :product="product" :params="params" :specs="specs" :badges="badgedParams" :rating-summary="ratingSummary" :rating-unavailable="!!ratingError" :rating-stale="ratingData?.stale"
   @reviews-changed="async()=>{await refreshNuxtData();reloadProduct({requestPolicy:'network-only'})}" @photo-removed="reloadProduct({requestPolicy:'network-only'})" @review="reviewModal({...product})" @photo="imagesModal(+id)" @similar="product.category && similarModal(+id,product.category.id)" @record="recordModal(+id)" @favorite="toggleFavorite" @remove-photo="removeImageConfirm" @remove-record="confirmRemoval('Удалить рекорд?',removeRecord,$event)" @remove-similar="confirmRemoval('Удалить связь похожести?',removeSimilar,$event)" />
  <CommunityPlantExperiments :plant-id="id"/>
  <PostContentAd v-if="product.status==='active'" :key="id" placement="plant" />
 </div>
</template>
